import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router';
import { useBroadcast } from '../contexts/BroadcastContext';
import './PersistentBroadcaster.css';

const PersistentBroadcaster: React.FC = () => {
  const {
    currentBroadcast,
    status,
    audioLevel,
    stopBroadcast,
  } = useBroadcast();

  const navigate = useNavigate();
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Monitor audio from the stage
  useEffect(() => {
    if (status === 'live' && !audioRef.current) {
      // Create audio element for monitoring
      audioRef.current = new Audio();
      audioRef.current.volume = 0.5;
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    };
  }, [status]);

  if (!currentBroadcast || status === 'idle') {
    return null;
  }

  const handleStopBroadcast = async () => {
    if (window.confirm('Are you sure you want to stop broadcasting?')) {
      stopBroadcast();
    }
  };

  const handleGoToLive = () => {
    navigate('/go-live');
  };

  const getStatusColor = () => {
    switch (status) {
      case 'live':
        return 'var(--status-success)';
      case 'connecting':
        return 'var(--status-warning)';
      case 'error':
        return 'var(--status-error)';
      default:
        return 'var(--text-secondary)';
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'live':
        return 'LIVE';
      case 'connecting':
        return 'CONNECTING...';
      case 'stopping':
        return 'STOPPING...';
      case 'error':
        return 'ERROR';
      default:
        return 'IDLE';
    }
  };

  return (
    <div className="persistent-broadcaster">
      <div className="broadcaster-content">
        {/* Status Indicator */}
        <div className="broadcaster-status">
          <div className="status-indicator">
            <span
              className={`status-dot ${status === 'live' ? 'pulse' : ''}`}
              style={{ backgroundColor: getStatusColor() }}
            />
            <span
              className="status-text"
              style={{ color: getStatusColor() }}
            >
              {getStatusText()}
            </span>
          </div>
          {status === 'live' && (
            <div className="audio-level-meter">
              <div
                className="audio-level-bar"
                style={{
                  width: `${audioLevel}%`,
                  transition: 'width 0.1s ease-out'
                }}
              />
            </div>
          )}
        </div>

        {/* Stream Info */}
        <div className="broadcaster-info" onClick={handleGoToLive}>
          <div className="broadcaster-icon">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <circle cx="12" cy="12" r="10"/>
              <circle cx="12" cy="12" r="4" fill="white"/>
            </svg>
          </div>
          <div className="broadcaster-details">
            <div className="broadcaster-name">{currentBroadcast.name}</div>
            <div className="broadcaster-mode">
              {currentBroadcast.streamingMode === 'browser' ? 'Browser Broadcast' : 'WHIP Broadcast'}
            </div>
          </div>
        </div>

        {/* Controls */}
        <div className="broadcaster-controls">
          <button
            className="btn btn-secondary btn-sm broadcaster-manage-btn"
            onClick={handleGoToLive}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
            Manage
          </button>

          <button
            className="btn btn-danger btn-sm broadcaster-stop-btn"
            onClick={handleStopBroadcast}
            disabled={status === 'stopping'}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <rect x="6" y="6" width="12" height="12" rx="2"/>
            </svg>
            Stop Broadcast
          </button>
        </div>
      </div>
    </div>
  );
};

export default PersistentBroadcaster;
